/* eslint-disable @typescript-eslint/no-unused-vars */
'use client';

import { CacheKey } from '@eliadmin/core/src/api/constants';
import { fetchReferences } from '@eliadmin/core/src/api/references/references-api';
import type { Reference } from '@eliadmin/core/src/api/references/references-types';
import { Button } from '@eliadmin/core/src/components/ui/button';
import { GenericLoader } from '@eliadmin/core/src/components/ui/generic-loader';
import { useTranslations } from '@eliadmin/core/src/hooks/use-translations';
import { useGlobalStore } from '@eliadmin/core/src/stores/global-store';
import { useLocaleStore } from '@eliadmin/core/src/stores/locale-store';
import { cn } from '@eliadmin/core/src/utils/style-utils';
import { GoogleMap, Marker, useJsApiLoader } from '@react-google-maps/api';
import { useSuspenseQuery } from '@tanstack/react-query';
import { useCallback, useState } from 'react';
import { FiArrowRight } from 'react-icons/fi';
import { LuChevronLeft, LuChevronRight } from 'react-icons/lu';
import { getControlLabels } from '../control-labels';

export function ReferenceMap() {
  const { 'google-api': googleApiKey } = useGlobalStore(state => state.apiKeys);

  const { isLoaded } = useJsApiLoader({
    id: 'google-map-script',
    googleMapsApiKey: googleApiKey?.token ?? '',
  });

  const [selectedPlace, setSelectedPlace] = useState<Reference | null>(null);

  const [currentPage, setCurrentPage] = useState(1);

  const { t } = useTranslations();

  const locale = useLocaleStore(state => state.currentLocale);
  const labels = getControlLabels(locale);

  const { data: references } = useSuspenseQuery({
    queryKey: [CacheKey.REFERENCES, locale],
    queryFn: () =>
      fetchReferences({
        locale,
        limit: 9999,
      }),
  });

  const maxPageButtonsToShow = 5;
  const totalPages = Math.ceil(references?.data?.length / 5);
  const startPage = Math.max(1, currentPage - Math.floor(maxPageButtonsToShow / 2));
  const endPage = Math.min(totalPages, startPage + maxPageButtonsToShow - 1);

  const [map, setMap] = useState<google.maps.Map | null>(null);

  const onUnmount = useCallback(function callback(map: google.maps.Map) {
    setMap(null);
  }, []);

  const onLoad = useCallback(function callback(map: google.maps.Map) {
    map.setZoom(4);

    setMap(map);
  }, []);

  const [center, setCenter] = useState({
    lat: 48.13881165313404,
    lng: 17.118919055143596,
  });

  return (
    <div
      className={cn(
        'grid grid-cols-1 grid-rows-2 sm:h-[500px] sm:grid-cols-2 sm:grid-rows-1',
        'ea__reference-map-wrapper',
      )}
    >
      <div
        className={cn(
          'flex flex-col gap-8 px-4 py-8 sm:h-full md:p-12',
          'ea__reference-map-content',
        )}
      >
        <div className={cn('flex flex-col', 'ea__reference-map-content__header')}>
          <span
            className={cn(
              'text-3xl font-bold text-foreground sm:text-4xl',
              'ea__reference-map-content__header__title',
            )}
          >
            {t('text724')}
          </span>
        </div>
        <div className={cn('flex flex-col gap-1', 'ea__reference-map-content__body')}>
          {references.data.slice((currentPage - 1) * 5, currentPage * 5).map(reference => (
            <button
              type="button"
              aria-pressed={selectedPlace?.id === reference.id}
              key={reference.id}
              className={cn(
                'flex w-full cursor-pointer items-center justify-between bg-secondary px-8 py-2 text-left',
                {
                  'bg-primary text-background': selectedPlace?.id === reference.id,
                },
                'ea__reference-map-content__body__item',
              )}
              onClick={() => {
                if (!reference.latitude || !reference.longitude) return;
                setSelectedPlace(reference);
                setCenter({
                  lat: +reference.latitude,
                  lng: +reference.longitude,
                });
              }}
            >
              <span className={cn('text-md', 'ea__reference-map-content__body__item__name')}>
                {reference.name}
              </span>
              {selectedPlace?.id === reference.id && <FiArrowRight size={20} />}
            </button>
          ))}
        </div>

        <div
          className={cn('flex items-center justify-center gap-1', 'ea__reference-map-pagination')}
        >
          <Button
            aria-label={labels.previous}
            className={cn(
              'h-9 !w-9 rounded-none p-1 text-primary',
              'ea__reference-map-pagination__left__button',
            )}
            variant="secondary"
            onClick={() => {
              if (currentPage === 1) return;
              setCurrentPage(currentPage - 1);
            }}
          >
            <LuChevronLeft className="h-4 w-4" />
          </Button>
          {Array.from({ length: endPage - startPage + 1 }).map((_, index) => (
            <Button
              key={`${index}`}
              className={cn(
                'h-9 !w-9 rounded-none border border-primary p-1 text-white',
                'ea__reference-map-pagination__button',
              )}
              variant={currentPage === index + startPage ? 'default' : 'secondary'}
              onClick={() => {
                setCurrentPage(index + startPage);
              }}
            >
              <div
                className={cn(
                  'cursor-pointer',
                  currentPage === index + startPage &&
                    'ea__reference-map-pagination__button__text--active !text-white',
                  'ea__reference-map-pagination__button__text text-primary',
                )}
              >
                {index + startPage}
              </div>
            </Button>
          ))}
          <Button
            aria-label={labels.next}
            className={cn(
              'h-9 !w-9 rounded-none p-1 text-primary',
              'ea__reference-map-pagination__right__button',
            )}
            variant="secondary"
            onClick={() => {
              if (currentPage === totalPages) return;
              setCurrentPage(currentPage + 1);
            }}
          >
            <LuChevronRight className="h-4 w-4" />
          </Button>
        </div>
      </div>
      <div
        className={cn(
          'aspect-square h-full w-full sm:aspect-auto',
          'ea__reference-map__map-wrapper',
        )}
      >
        {isLoaded ? (
          <GoogleMap
            mapContainerStyle={{
              width: '100%',
              height: '100%',
            }}
            center={center}
            onLoad={onLoad}
            onUnmount={onUnmount}
          >
            {references.data.map(place => {
              if (place.latitude && place.longitude)
                return (
                  <Marker
                    key={place.id}
                    position={{
                      lat: +place.latitude,
                      lng: +place.longitude,
                    }}
                    onClick={() => {
                      setCenter({
                        lat: +place.latitude,
                        lng: +place.longitude,
                      });
                      setSelectedPlace(place);
                      const placeIndex = references.data.findIndex(ref => ref.id === place.id);

                      const newPage = Math.ceil((placeIndex + 1) / 5);

                      setCurrentPage(newPage);
                    }}
                  />
                );
            })}
          </GoogleMap>
        ) : (
          <GenericLoader />
        )}
      </div>
    </div>
  );
}
